import { Stack } from '@mui/material';
import { Audition } from 'hooks/u005/useGetAuditionHooks';
import { Section, SECTION_TYPE } from 'atoms/Section';
import { MediaQueryEnum, useMediaQuery } from 'commons/utils';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { CopyButton } from 'atoms/CopyButton';

type GroupMember = {
  consenter: string;
  display_name: string;
  notice_flg: string;
  admin_approval: string;
};

type GroupAuditionInfoProps = {
  audition: Audition;
};

export const GroupAuditionInfo = ({ audition }: GroupAuditionInfoProps) => {
  const isSp = useMediaQuery(MediaQueryEnum.sp);
  const { t: tAuditionInfo } = useTranslationWithLang('auditionInfo');
  const renderGroupMemberInfo = (groupMembers: GroupMember[]) => {
    const adults = groupMembers
      .filter((member) => member.consenter === '2')
      .map((member) => member.display_name);
    const minors = groupMembers
      .filter((member) => member.consenter === '3')
      .map((member, index) => ({
        display_name: member.display_name,
        notice_flg: member.notice_flg,
        admin_approval: member.admin_approval,
        index: index,
      }));

    return (
      <>
        <div className="mt-8">
          <span>{tAuditionInfo('TU005_044')}</span>
          {minors.length > 0 ? (
            <span>
              {minors.length}
              {tAuditionInfo('TU005_030')}
            </span>
          ) : null}
        </div>
        <div className="mt-2 mb-4">
          {minors.length > 0 ? (
            minors.map((minor, index) => (
              <div className="ml-2 mt-2 flex justify-between" key={index}>
                <span className="break-all mr-4">{minor.display_name}</span>
                <span className="shrink-0">
                  {minor.admin_approval === '2' ? (
                    <span className="text-[--c-error]">{tAuditionInfo('TU005_048')}</span>
                  ) : minor.notice_flg === '1' ? (
                    tAuditionInfo('TU005_031')
                  ) : (
                    <span className="text-[--c-error]">{tAuditionInfo('TU005_032')}</span>
                  )}
                </span>
              </div>
            ))
          ) : (
            <div>{tAuditionInfo('TU005_033')}</div>
          )}
        </div>
        <div className="mt-8">
          {tAuditionInfo('TU005_038')}
          {adults.length > 0 ? (
            <span>
              {adults.length}
              {tAuditionInfo('TU005_030')}
            </span>
          ) : null}
        </div>
        <div className="mt-2 mb-8">
          {adults.length > 0 ? adults.join('、') : <div>{tAuditionInfo('TU005_033')}</div>}
        </div>
      </>
    );
  };

  if (isSp) {
    return (
      <>
        <div className="flex items-center w-full h-full py-6">
          <div className="flex h-fit w-full">
            <Section type={SECTION_TYPE.GROUP_INFO}>
              <Stack className="h-full p-4 overflow-y-auto overflow-x-hidden">
                <div className="text-[--c-text]">
                  {tAuditionInfo('TU005_034', { audition_title: audition.audition_info.title })}
                </div>
                {audition.group[0].group_approval === '1' ? (
                  <div className="mt-8 text-[--c-error]">
                    {tAuditionInfo('TU005_035')}
                    <a
                      href={audition.next_path}
                      className="text-[--c-error] break-all block mt-2 cursor-pointer hover:underline"
                      aria-label={tAuditionInfo('TU005_035')}
                    >
                      {audition.next_path}
                    </a>
                  </div>
                ) : (
                  <div className="mt-8">
                    {tAuditionInfo('TU005_036')}
                    <div className="flex items-center mt-2">
                      <p className="text-[--c-text] break-all block">
                        {audition.next_path}
                        <span className="ml-2">
                          <CopyButton copyText={audition.next_path} />
                        </span>
                      </p>
                    </div>
                    <p className="text-[--c-text] break-all block mt-2">
                      {tAuditionInfo('TU005_050', {
                        year: new Date(audition.audition_info.end_dtm).getFullYear(),
                        month: (new Date(audition.audition_info.end_dtm).getMonth() + 1)
                          .toString()
                          .padStart(2, '0'),
                        day: new Date(audition.audition_info.end_dtm)
                          .getDate()
                          .toString()
                          .padStart(2, '0'),
                        hour: new Date(audition.audition_info.end_dtm)
                          .getHours()
                          .toString()
                          .padStart(2, '0'),
                        minute: new Date(audition.audition_info.end_dtm)
                          .getMinutes()
                          .toString()
                          .padStart(2, '0'),
                      })}
                    </p>
                  </div>
                )}
                <div className="mt-8">
                  {tAuditionInfo('TU005_037', { member_num: audition.group[0].member_num })}
                </div>
                {audition.group_member && renderGroupMemberInfo(audition.group_member)}
              </Stack>
            </Section>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center w-[70%] h-full py-6">
        <div className="flex h-fit px-4 w-full">
          <Section type={SECTION_TYPE.GROUP_INFO}>
            <Stack className="h-full p-8 h-auto">
              <div className="text-[--c-text]">
                {tAuditionInfo('TU005_034', { audition_title: audition.audition_info.title })}
              </div>
              {audition.group[0].group_approval === '1' ? (
                <div className="mt-8 text-[--c-error]">
                  {tAuditionInfo('TU005_035')}
                  <a
                    href={audition.next_path}
                    className="text-[--c-error] break-all block mt-2 cursor-pointer hover:underline"
                    aria-label={tAuditionInfo('TU005_035')}
                  >
                    {audition.next_path}
                  </a>
                </div>
              ) : (
                <div className="mt-8">
                  {tAuditionInfo('TU005_036')}
                  <div className="flex items-center mt-2">
                    <p className="text-[--c-text] break-all block">
                      {audition.next_path}
                      <span className="ml-2">
                        <CopyButton copyText={audition.next_path} />
                      </span>
                    </p>
                  </div>
                  <p className="text-[--c-text] break-all block mt-2">
                    {tAuditionInfo('TU005_050', {
                      year: new Date(audition.audition_info.end_dtm).getFullYear(),
                      month: (new Date(audition.audition_info.end_dtm).getMonth() + 1)
                        .toString()
                        .padStart(2, '0'),
                      day: new Date(audition.audition_info.end_dtm)
                        .getDate()
                        .toString()
                        .padStart(2, '0'),
                      hour: new Date(audition.audition_info.end_dtm)
                        .getHours()
                        .toString()
                        .padStart(2, '0'),
                      minute: new Date(audition.audition_info.end_dtm)
                        .getMinutes()
                        .toString()
                        .padStart(2, '0'),
                    })}
                  </p>
                </div>
              )}
              <div className="mt-8">
                {tAuditionInfo('TU005_037', { member_num: audition.group[0].member_num })}
              </div>
              {audition.group_member && renderGroupMemberInfo(audition.group_member)}
            </Stack>
          </Section>
        </div>
      </div>
    </>
  );
};
